import {
	CurrencyEnum,
	OrderPaymentMethod,
	OrderShippingMethod,
	useCreateOrderPaymentMutation,
	useGetProductPaymentPreviewQuery,
} from '@codezo/graphql';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { CheckCircle2, CreditCard, FileText, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { PaymentTotals, money } from './PaymentTotals';

/* ── Types ── */

interface OrderItem {
	id: string;
	productName?: string | null;
	productImages?: Array<{ url?: string | null }> | null;
	quantity: number;
	status: string;
	unitPrice?: number | null;
	totalPrice?: number | null;
	adjustedTotalPrice?: number | null;
	delivery?: {
		shippingMethod: OrderShippingMethod;
		totalPrice?: number | null;
	} | null;
	sample?: {
		status: string;
		totalPrice?: number | null;
	} | null;
}

interface PaymentMethodOption {
	method: OrderPaymentMethod;
	available: boolean;
	reason?: string | null;
}

interface PayForProductionModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orderId: string;
	items: OrderItem[];
	orderTotal?: number | null;
	paymentMethods?: PaymentMethodOption[] | null;
	/** PO captured at acceptance — pre-fills the checkout PO field (still editable). */
	orderPoNumber?: string | null;
	onSuccess?: () => void;
}

type Step = 'summary' | 'checkout' | 'complete';

/**
 * One line inside a breakdown: what it is, and what it costs.
 *
 * Shipping arrives in the same `{ method, amount }` shape whether it belongs to
 * an item or to the order, so both render through this — an order-level shipment
 * is not a different kind of cost, just one no single product can claim.
 */
function BreakdownRow({
	label,
	amount,
	currency,
}: {
	label: string;
	amount: number;
	currency: string;
}) {
	return (
		<div className="flex items-center justify-between">
			<span className="text-text-4">{label}</span>
			<span className="text-text-3">
				{currency} {money(amount)}
			</span>
		</div>
	);
}

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';
let stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripe() {
	if (!stripePromise && STRIPE_KEY) stripePromise = loadStripe(STRIPE_KEY);
	return stripePromise;
}

/* ════════════════════════ MODAL ════════════════════════ */

export function PayForProductionModal({
	open,
	onOpenChange,
	orderId,
	items,
	paymentMethods,
	orderPoNumber,
	onSuccess,
}: PayForProductionModalProps) {
	const stripeOption = useMemo(
		() => paymentMethods?.find((m) => m.method === OrderPaymentMethod.Stripe),
		[paymentMethods]
	);
	const invoiceOption = useMemo(
		() => paymentMethods?.find((m) => m.method === OrderPaymentMethod.Invoice),
		[paymentMethods]
	);

	const defaultMethod = stripeOption?.available
		? OrderPaymentMethod.Stripe
		: invoiceOption?.available
			? OrderPaymentMethod.Invoice
			: OrderPaymentMethod.Stripe;
	const [paymentMethod, setPaymentMethod] = useState<OrderPaymentMethod>(defaultMethod);
	const [createPayment, { loading }] = useCreateOrderPaymentMutation();

	const [step, setStep] = useState<Step>('summary');
	const [clientSecret, setClientSecret] = useState<string | null>(null);
	const [poNumber, setPoNumber] = useState(orderPoNumber ?? '');

	// Pre-fill the PO from the order each time the modal opens (the client can
	// still edit it — whatever they leave here wins for this invoice).
	useEffect(() => {
		if (open) setPoNumber(orderPoNumber ?? '');
	}, [open, orderPoNumber]);

	// Payment currency is fixed by method: Stripe always charges EUR, invoice always DKK.
	// The preview reflects the currency the customer will actually be billed in, not the
	// global currency selector.
	const previewCurrency =
		paymentMethod === OrderPaymentMethod.Stripe ? CurrencyEnum.Eur : CurrencyEnum.Dkk;

	// Fetch preview with real pricing. Keep the previous result visible while a
	// method switch refetches in the new currency — otherwise the whole breakdown
	// collapses to a spinner and re-expands on every toggle (jarring flicker).
	const {
		data: previewData,
		previousData: prevPreviewData,
		loading: previewLoading,
	} = useGetProductPaymentPreviewQuery({
		// The same ids `createOrderPayment` is handed below. The preview used to
		// price the whole order regardless of the selection, which agreed with the
		// charge only while the selection WAS the whole order — so paying for one
		// product quoted the total for every payable one.
		variables: {
			orderId,
			currency: previewCurrency,
			orderItemIds: items.map((i) => i.id),
		},
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const preview =
		previewData?.getProductPaymentPreview ?? prevPreviewData?.getProductPaymentPreview;

	const reset = () => {
		setStep('summary');
		setClientSecret(null);
		setPaymentMethod(defaultMethod);
		setPoNumber(orderPoNumber ?? '');
	};

	const handleClose = (next: boolean) => {
		if (!next) {
			if (step === 'complete') onSuccess?.();
			reset();
		}
		onOpenChange(next);
	};

	const handleContinue = async () => {
		try {
			const { data } = await createPayment({
				variables: {
					input: {
						orderId,
						orderItemIds: items.map((i) => i.id),
						paymentMethod,
						poNumber: poNumber.trim() || undefined,
						returnUrl: window.location.href,
					},
				},
			});

			const result = data?.createOrderPayment;
			if (!result?.success) {
				toast.error(result?.message || 'Failed to create payment');
				return;
			}

			if (result.paymentMethod === OrderPaymentMethod.Stripe && result.clientSecret) {
				setClientSecret(result.clientSecret);
				setStep('checkout');
			} else if (result.paymentMethod === OrderPaymentMethod.Invoice) {
				toast.success(result.message || 'Invoice booked. You will receive it by email.');
				handleClose(false);
				onSuccess?.();
			} else {
				toast.success(result.message || 'Payment created');
				handleClose(false);
				onSuccess?.();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create payment');
		}
	};

	const handleCheckoutComplete = () => {
		setStep('complete');
		onSuccess?.();
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-lg"
				showCloseButton={step !== 'checkout'}
				{...(step === 'checkout'
					? {
							onEscapeKeyDown: (e: Event) => e.preventDefault(),
							onPointerDownOutside: (e: Event) => e.preventDefault(),
						}
					: {})}
			>
				<DialogHeader>
					<DialogTitle>
						{step === 'summary' && 'Pay for production'}
						{step === 'checkout' && 'Checkout'}
						{step === 'complete' && 'Payment confirmed'}
					</DialogTitle>
				</DialogHeader>

				<DialogBody className="scroll-thin max-h-[70vh] overflow-y-auto p-0">
					{/* ── Summary step ── */}
					{step === 'summary' && (
						<div className="flex flex-col">
							{/* Payment method picker */}
							<div className="border-b border-line-1 px-4 py-3">
								<div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
									Payment method
								</div>
								<div className="flex gap-1.5">
									<button
										type="button"
										disabled={stripeOption?.available === false}
										onClick={() => setPaymentMethod(OrderPaymentMethod.Stripe)}
										className={cn(
											'flex flex-1 items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors',
											paymentMethod === OrderPaymentMethod.Stripe
												? 'border-accent bg-accent-soft text-accent'
												: 'border-line-2 text-text-3 hover:border-line-3',
											stripeOption?.available === false &&
												'cursor-not-allowed opacity-40'
										)}
									>
										<CreditCard size={14} />
										Card (EUR)
									</button>
									<button
										type="button"
										disabled={invoiceOption?.available === false}
										onClick={() => setPaymentMethod(OrderPaymentMethod.Invoice)}
										className={cn(
											'flex flex-1 items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors',
											paymentMethod === OrderPaymentMethod.Invoice
												? 'border-accent bg-accent-soft text-accent'
												: 'border-line-2 text-text-3 hover:border-line-3',
											invoiceOption?.available === false &&
												'cursor-not-allowed opacity-40'
										)}
									>
										<FileText size={14} />
										Invoice (DKK)
									</button>
								</div>
								{stripeOption?.available === false && stripeOption.reason && (
									<p className="mt-2 text-xs text-status-warn">
										Card: {stripeOption.reason}
									</p>
								)}
								{invoiceOption?.available === false && invoiceOption.reason && (
									<p className="mt-2 text-xs text-status-warn">
										Invoice: {invoiceOption.reason}
									</p>
								)}
								<p className="mt-2 text-xs text-text-4">
									{paymentMethod === OrderPaymentMethod.Stripe
										? 'Pay securely via Stripe. Amount will be charged in EUR.'
										: 'An e-conomic invoice will be generated and sent to the invoice recipient.'}
								</p>
							</div>

							{/* PO number */}
							<div className="border-b border-line-1 px-4 py-3">
								<label
									htmlFor="production-po-number"
									className="mb-2 block text-xs font-medium uppercase tracking-[0.06em] text-text-4"
								>
									PO number{' '}
									<span className="text-text-4 normal-case">(optional)</span>
								</label>
								<Input
									id="production-po-number"
									value={poNumber}
									onChange={(e) => setPoNumber(e.target.value)}
									placeholder="e.g. PO-12345"
								/>
							</div>

							{/* Order items with preview breakdown */}
							{!preview && previewLoading ? (
								<div className="flex items-center justify-center py-10">
									<Loader2 size={20} className="animate-spin text-text-3" />
								</div>
							) : preview ? (
								<div
									className={cn(
										'transition-opacity',
										previewLoading && 'opacity-50'
									)}
								>
									<div className="border-b border-line-1">
										<div className="px-4 pt-3 pb-1 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
											Items ({preview.items.length})
										</div>
										{preview.items.map((line, i) => (
											<div
												key={line.orderItemId}
												className="px-4 py-3"
												style={{
													borderBottom:
														i < preview.items.length - 1
															? '0.5px solid var(--line-1)'
															: 'none',
												}}
											>
												<div className="mb-1.5 flex items-center justify-between">
													<span className="text-sm font-medium text-text-1">
														{line.description}
													</span>
													<span className="text-sm font-medium text-text-1">
														{preview.currency}{' '}
														{money(line.lineNetAmount)}
													</span>
												</div>
												<div className="flex flex-col gap-0.5 text-xs">
													<BreakdownRow
														label="Products"
														amount={line.productsAmount}
														currency={preview.currency}
													/>
													{line.fees.map((fee) => (
														<BreakdownRow
															key={fee.name}
															label={fee.name}
															amount={fee.amount}
															currency={preview.currency}
														/>
													))}
													{line.deliveries.map((del) => (
														<BreakdownRow
															key={del.method}
															label={`Delivery (${del.method})`}
															amount={del.amount}
															currency={preview.currency}
														/>
													))}
													{/* Already paid for as samples, credited back
													    here. The amounts arrive positive because
													    they are subtracted, so negate them for
													    display. Named per sample so the client can
													    see which one paid for what. */}
													{line.deductibleSamples.map((d) => (
														<BreakdownRow
															key={d.sampleId}
															label={d.description}
															amount={-d.amount}
															currency={preview.currency}
														/>
													))}
												</div>
											</div>
										))}
									</div>

									{/* Shipping that belongs to the order rather than to any one
									    product — a combination plan's shipments, each carrying
									    several items. It counts toward grandTotal but sits in no
									    item line, so without this section the rows above simply
									    don't add up to the total. */}
									{preview.deliveries.length > 0 && (
										<div className="border-b border-line-1 px-4 py-3">
											<div className="mb-1.5 flex items-center justify-between">
												<span className="text-sm font-medium text-text-1">
													Shipping
												</span>
												<span className="text-sm font-medium text-text-1">
													{preview.currency}{' '}
													{money(
														preview.deliveries.reduce(
															(sum, d) => sum + d.amount,
															0
														)
													)}
												</span>
											</div>
											<div className="mb-1 text-xs text-text-4">
												These shipments carry more than one product, so they
												are priced for the order.
											</div>
											<div className="flex flex-col gap-0.5 text-xs">
												{preview.deliveries.map((del) => (
													<BreakdownRow
														key={del.method}
														label={del.method}
														amount={del.amount}
														currency={preview.currency}
													/>
												))}
											</div>
										</div>
									)}

									{/* Why this invoice's credit is smaller than the order's:
									    the rest belongs to products being paid later. Stated
									    outright so the difference doesn't read as a mistake. */}
									{preview.deductionsDeferred > 0 && (
										<div className="border-b border-line-1 px-4 py-3 text-xs text-text-3">
											{preview.currency} {money(preview.deductionsDeferred)}{' '}
											of sample credit applies to products on a later invoice.
										</div>
									)}

									{/* Net / VAT / gross — the total is what will be charged */}
									<PaymentTotals
										currency={preview.currency}
										grandTotal={preview.grandTotal}
										vatRate={preview.vatRate}
										vatAmount={preview.vatAmount}
										grossAmount={preview.grossAmount}
									/>
								</div>
							) : (
								<div className="px-4 py-8 text-center text-sm text-text-4">
									Unable to load pricing preview.
								</div>
							)}
						</div>
					)}

					{/* ── Stripe checkout step ── */}
					{step === 'checkout' && (
						<div className="min-h-[420px] px-4 pb-4">
							{clientSecret && getStripe() ? (
								<EmbeddedCheckoutProvider
									key={clientSecret}
									stripe={getStripe()!}
									options={{
										clientSecret,
										onComplete: handleCheckoutComplete,
									}}
								>
									<EmbeddedCheckout />
								</EmbeddedCheckoutProvider>
							) : (
								<div className="flex min-h-[360px] flex-col items-center justify-center gap-3">
									<Loader2 size={24} className="animate-spin text-text-3" />
									<p className="text-base text-text-3">
										Preparing secure checkout…
									</p>
								</div>
							)}
						</div>
					)}

					{/* ── Complete step ── */}
					{step === 'complete' && (
						<div className="flex flex-col items-center gap-4 py-12 text-center">
							<div className="grid h-12 w-12 place-items-center rounded-full bg-status-success/10">
								<CheckCircle2 size={24} className="text-status-success" />
							</div>
							<div>
								<div className="text-lg font-medium text-text-1">
									Payment confirmed
								</div>
								<p className="mt-1 text-base text-text-3">
									Your payment has been processed successfully.
								</p>
							</div>
						</div>
					)}
				</DialogBody>

				{/* Footer — only on summary and complete */}
				{step === 'summary' && (
					<DialogFooter>
						<Button variant="outline" onClick={() => handleClose(false)}>
							Cancel
						</Button>
						<Button
							onClick={handleContinue}
							disabled={
								loading ||
								(paymentMethod === OrderPaymentMethod.Stripe &&
									stripeOption?.available === false) ||
								(paymentMethod === OrderPaymentMethod.Invoice &&
									invoiceOption?.available === false)
							}
						>
							{loading
								? 'Processing…'
								: paymentMethod === OrderPaymentMethod.Stripe
									? 'Continue to payment'
									: 'Continue to invoice'}
						</Button>
					</DialogFooter>
				)}

				{step === 'complete' && (
					<DialogFooter>
						<Button onClick={() => handleClose(false)}>Done</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);
}
